/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import * as _ from 'underscore';
import * as moment from 'moment';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '@app/common/app-config.service';

@Injectable({
  providedIn: 'root'
})
export class DataTransformationService {
  isNull(val) {
    return val === 'null' || val === null || val === '' || typeof val === 'undefined' || val === 'undefined';
  }
  constructor(private translate: TranslateService, private appConfig: AppConfigService, private util: UtilService) {}

  getFormattedDOB(birthDate, dateFormat) {
    // format dob and return blank if dob does not have valid format
    const match = /^(\d{4})[-/](\d{2})[-/](\d{2})$/.exec(birthDate); // Validate for YYYY-MM-DD and YYYY/MM/DD.
    if (match === null) {
      birthDate = '';
    } else {
      birthDate = this.getFormattedDate(birthDate, dateFormat);
    }
    return birthDate;
  }

  formatDateTimeString(dateTimeString) {
    let returnDateTime = 'NA';
    try {
      const match = dateTimeString.match(
        /^(\d{4})[-/](\d{2})[-/](\d{2}) (\d{2}):(\d{2}):(\d{2})?.(\d{1}|\d{2}|\d{3})$/
      );
      if (match === null) {
        throw new Error('Study date time is invalid!');
      }
      const dt = dateTimeString.toString().split(/[/\-|\s]/);
      const year = dt[0];
      const dd = this.convertToTwoDigit(dt[2]);
      const month = this.convertToTwoDigit(parseInt(dt[1], 10));
      const time = dt[3].toString().split(/[.:]/);
      returnDateTime =
        year +
        appConstants.DATE_SEPARATOR +
        month +
        appConstants.DATE_SEPARATOR +
        dd +
        appConstants.SPACE +
        time[0] +
        appConstants.TIME_SEPARATOR +
        time[1] +
        appConstants.TIME_SEPARATOR +
        time[2];
    } catch (err) {
      return 'NA';
    }
    return returnDateTime;
  }

  /* i18n - 'No Patient Name Available' string has been translated in respective module files where they are
   invoking this function */
  patientName(fullName) {
    if (UtilService.isNull(fullName)) {
      return 'No Patient Name Available';
    } else {
      let name = '';
      if (!UtilService.isNull(fullName.family)) {
        name = fullName.family.toUpperCase();
      }

      if (!UtilService.isNull(fullName.given)) {
        if (!UtilService.isNull(name)) {
          name = name + ', ';
        }
        name =
          name +
          fullName.given.replace(/\w\S*/g, txt => {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
          });
      }

      if (!UtilService.isNull(name)) {
        return name;
      } else if (!UtilService.isNull(fullName.text)) {
        return fullName.text;
      } else {
        return 'No Patient Name Available';
      }
    }
  }

  /* i18n - 'No Provider Name Available' string has not been translated since in
  workload-manager/list-container/directives/physician-name.js they are comparing this value */
  providerName(providerName) {
    let resultName = 'providerName.unavailable';
    if (!UtilService.isNull(providerName)) {
      const name = [];

      if (!UtilService.isNull(providerName.family)) {
        name.push(providerName.family);
      }
      if (!UtilService.isNull(providerName.given)) {
        name.push(providerName.given);
      }

      const result = _.chain(name).flatten().reduce(this.reduceContentfulStrings, []).value();

      if (result && result.length !== 0) {
        resultName = result.join(', ');
      } else if (!UtilService.isNull(providerName.text)) {
        resultName = providerName.text;
      }
    }
    return resultName === 'Unspecified' ? 'label.unspecified' : resultName;
  }

  convertToTwoDigit(entry): any {
    if (entry.toString().length < 2) {
      entry = '0' + entry; // Appending zero if it is single digit.
    }
    return entry;
  }

  stylePopoverTitlePhysician(physicianName, physicianRole) {
    return '<h3>' + physicianName + '</h3>' + '<h4><i>' + physicianRole + '</i></h4>';
  }

  // i18n - This function has not been used anywhere, hence not translated
  stylePopoverTextPhoneNumbers(teleCom) {
    if (
      UtilService.isNull(teleCom) ||
      (UtilService.isNull(teleCom.home) && UtilService.isNull(teleCom.work) && UtilService.isNull(teleCom.cell))
    ) {
      return '<h4>No phone information available</h4>';
    } else {
      return (
        '<h4>' +
        'Home: ' +
        '</h4>' +
        teleCom.home +
        '<br>' +
        '<h4>' +
        'Work: ' +
        '</h4>' +
        teleCom.work +
        '<br>' +
        '<h4>' +
        'Mobile: ' +
        '</h4>' +
        teleCom.cell
      );
    }
  }

  getFormattedDate(date, format) {
    return moment(date).format(format);
  }

  isTodayDate(dateStr) {
    const today = new Date();
    const date = moment(dateStr);
    return today.getFullYear() === date.year() && today.getMonth() === date.month() && today.getDate() === date.date();
  }

  displayLimitedDigits(item, maxLength) {
    if (item.length > maxLength) {
      item = item.substr(item.length - maxLength);
    }
    return item;
  }

  filterDomainIdentifier(userDomain, domainMrnMap) {
    const unavailable = 'Unavailable';
    let userDomainIdentifier = unavailable;
    let userDomainIdentifierName = '';
    let validIdentifierCount = 0;
    const domainIdentifierList = [];

    if (domainMrnMap !== undefined || domainMrnMap !== null) {
      _.each(domainMrnMap, (identifier: any) => {
        if (identifier.value && identifier.value.trim() !== '') {
          ++validIdentifierCount;

          // REMOTE Exams does not provide system for identifier
          if (identifier.system === undefined) {
            userDomainIdentifier = identifier.value.trim();
          } else if (identifier.system !== userDomain) {
            domainIdentifierList.push(identifier);
          } else if (UtilService.isContentfulString(identifier.value)) {
            userDomainIdentifier = identifier.value.trim();
            userDomainIdentifierName = identifier.system;
          }
        }
      });
    }
    return {
      domainIdentifier: userDomainIdentifier,
      domainIdentifierName: userDomainIdentifierName,
      identifierListLength: validIdentifierCount,
      identifierCountDisplayStatus: userDomainIdentifier === unavailable || validIdentifierCount > 1,
      otherDomainIdentifierList: domainIdentifierList
    };
  }

  userDtoToDisplayName(user) {
    if (user.firstName.length > 0 && user.lastName.length > 0) {
      return user.firstName + ' ' + user.lastName;
    } else if (user.firstName.length > 0) {
      return user.firstName;
    } else {
      return user.lastName;
    }
  }

  isValidDates(fromDate, toDate) {
    const from = moment(fromDate, 'YYYY/MM/DD');
    const to = moment(toDate, 'YYYY/MM/DD');
    return !from || !to || !from.isValid() || !to.isValid() || to.isSame(from) || to.isBefore(from) ? false : true;
  }

  calculateStudyAge(fromDate, toDate) {
    let age;

    const from = moment(fromDate);
    const to = moment(toDate);

    const duration = moment.duration(to.diff(from));
    const days = Math.floor(duration.asDays());
    const months = Math.floor(duration.asMonths());
    const years = Math.floor(duration.asYears());

    const diffMonths = to.diff(from, 'months');
    from.add(diffMonths, 'months');
    const diffDays = to.diff(from, 'days');

    // Display study Date in different formats based on age difference
    if (days < 30) {
      age = days + this.translate.instant('acronym.day');
    } else if (years < 2) {
      age =
        diffDays === 0
          ? diffMonths + this.translate.instant('acronym.month')
          : diffMonths +
            this.translate.instant('acronym.month') +
            ' ' +
            diffDays +
            this.translate.instant('acronym.day');
    } else if (years < 18) {
      const monthInc = months - years * 12;
      age =
        monthInc === 0
          ? years + this.translate.instant('acronym.year')
          : years + this.translate.instant('acronym.year') + ' ' + monthInc + this.translate.instant('acronym.month');
    } else {
      age = years + this.translate.instant('acronym.year');
    }
    return age;
  }

  extractNumberOfDays(fromDate, toDate) {
    const from = moment(fromDate);
    const to = moment(toDate);

    const duration = moment.duration(to.diff(from));
    const days = Math.floor(duration.asDays());

    return days;
  }

  getExamStatusByStatusId(statusId) {
    return appConstants.examStatusByStatusIdMap[statusId];
  }

  private reduceContentfulStrings(memo, str) {
    const matches = str.match(/\w\S*/g);
    if (!matches) {
      return memo;
    }
    matches.forEach(newString => {
      memo.push(newString);
    });

    return memo;
  }
  /**
   *  Function to format the NHI id based on the format in the current NHI config
   *  nhiId - NHI id
   */
  getFormattedNHI(nhiId: string) {
    return this.util.formatNHI(nhiId, this.appConfig.getNHIPattern(), ' ');
  }
  /**
   *  Function to remove format from the NHI id if formatted as "333 333 4444" or "333-333-4444"
   *  nhiId - NHI id
   */
  getNonFormattedNHI(nhiId: string) {
    return this.util.formatNHI(nhiId, this.appConfig.getNHIPattern(), '');
  }

  /**
   * function to format patient age based on Locale
   */
  getFormattedPatAge(patAge) {
    if (patAge) {
      if (patAge.match(/[mdy]/gi)) {
        patAge = patAge.replace(/[m]/gi, this.translate.instant('acronym.month'));
        patAge = patAge.replace(/[d]/gi, this.translate.instant('acronym.day'));
        patAge = patAge.replace(/[y]/gi, this.translate.instant('acronym.year'));
      }
    }
    return patAge;
  }
}
